import { ReactComponent as EditIcon } from '../assets/icons/edit-dark.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete-outlined.svg';
import { PERIOD_FILTER } from '../constants/filters';
import { PRESETS } from '../constants/presets';
import { PERMISSION_CODES, PERMISSION_SCOPES } from '../constants/permissions';
import {
  TO_DO_ENTITIES,
  MAX_MOOD_LENGTH,
  MOOD_PLACEHOLDER,
  DASHBOARD_1_1_TABS,
  DASHBOARD_PLAN_TABS,
  CARD_TIME_DATE_FORMAT,
  CARD_DATE_FORMAT,
  DASHBOARD_TASKS_PRESET,
} from '../constants/dashboard';
import { getItemById, isArrayEmpty, isItemInList } from './helpers';
import { hasRequiredScopeCheck } from './permissions';
import { formatDate } from './dateUtils';

export const prepareDashboardPresets = presets => {
  if (!presets?.[PRESETS.DASHBOARD]) {
    return { teamPresets: [], tasksPreset: {} };
  }

  return presets[PRESETS.DASHBOARD].reduce(
    (acc, preset) => {
      if (preset.name !== DASHBOARD_TASKS_PRESET) {
        return {
          ...acc,
          teamPresets: [...acc.teamPresets, { ...preset }],
          tasksPreset: { ...acc.tasksPreset },
        };
      }

      return {
        ...acc,
        teamPresets: [...acc.teamPresets],
        tasksPreset: { ...preset },
      };
    },
    { teamPresets: [], tasksPreset: {} }
  );
};

export const getDasboardPeriodOptions = labels =>
  PERIOD_FILTER.map(period => ({
    ...period,
    label: labels[period.key],
  }));

export const prepareToDoItems = (
  items,
  surveyTypes,
  canAccessUserProfileCheck
) => {
  const { SURVEY, PLAN } = TO_DO_ENTITIES;

  return items.map(item => {
    if (item.entity === SURVEY) {
      return {
        ...item,
        type: getItemById(surveyTypes, item.type),
        expiryDate: formatDate(item.expire_at, CARD_TIME_DATE_FORMAT),
      };
    }

    if (item.entity === PLAN) {
      return {
        ...item,
        isAccessible: canAccessUserProfileCheck(item.user.id),
        proposedOn: formatDate(item.created_on, CARD_DATE_FORMAT),
      };
    }

    return {
      ...item,
      isAccessible: canAccessUserProfileCheck(item.user.id),
      requestedOn: formatDate(item.created_at, CARD_DATE_FORMAT),
    };
  });
};

export const prepareUserMoodData = moodResults => {
  const name = 'Mood';

  if (isArrayEmpty(moodResults)) {
    return [...Array(MAX_MOOD_LENGTH)].map((_, index) => ({
      name: `${name} ${index + 1}`,
      ...MOOD_PLACEHOLDER,
    }));
  }

  if (moodResults.length < MAX_MOOD_LENGTH) {
    const difference = MAX_MOOD_LENGTH - moodResults.length;
    const lastValueIndex = moodResults.length - 1;
    const lastValue = moodResults[lastValueIndex];

    return [...moodResults, ...Array(difference)].map((mood, index) => {
      if (!mood) {
        return {
          name: `${name} ${index + 1}`,
          placeholderValue: lastValue,
        };
      }

      return {
        name: `${name} ${index + 1}`,
        value: mood,
        placeholderValue: index === lastValueIndex ? mood : undefined,
      };
    });
  }

  return moodResults.map((mood, index) => ({
    name: `${name} ${index + 1}`,
    value: mood,
  }));
};

export const prepareMyTeamMenuItems = (labels, onEdit, onDelete, team) => [
  {
    type: 'edit',
    label: labels.edit,
    shouldItemRender: () => true,
    action: onEdit(team),
    icon: EditIcon,
  },
  {
    type: 'delete',
    label: labels.delete,
    action: onDelete,
    shouldItemRender: () => true,
    icon: DeleteIcon,
  },
];

export const prepareMyTeams = (
  myTeamLabel,
  myDirectReporters,
  teamPresets,
  availableUsers
) => {
  const teams = teamPresets
    ? [...teamPresets].reverse().reduce((acc, team) => {
        const {
          data: { users },
        } = team;
        const availableTeamUsers = users.filter(user =>
          isItemInList(availableUsers, user)
        );

        if (availableTeamUsers.length !== 0) {
          return [
            ...acc,
            {
              ...team,
              count: availableTeamUsers.length,
              users: availableTeamUsers,
              isEditable: true,
            },
          ];
        }

        return acc;
      }, [])
    : [];

  return [
    ...(!isArrayEmpty(myDirectReporters)
      ? [
          {
            id: 'my-team',
            name: myTeamLabel,
            users: myDirectReporters,
            count: myDirectReporters.length,
            isEditable: false,
          },
        ]
      : []),
    ...teams,
  ];
};

export const prepareOneOnOneTabs = (labels, accessControl) => {
  const hasSelfScope = hasRequiredScopeCheck(
    PERMISSION_CODES.ONE_ON_ONE_VIEW,
    PERMISSION_SCOPES.SELF,
    accessControl
  );
  const hasGlobalScope = hasRequiredScopeCheck(
    PERMISSION_CODES.ONE_ON_ONE_VIEW,
    PERMISSION_SCOPES.GLOBAL,
    accessControl
  );
  const hasDRScope = hasRequiredScopeCheck(
    PERMISSION_CODES.ONE_ON_ONE_VIEW,
    PERMISSION_SCOPES.DR,
    accessControl
  );

  return DASHBOARD_1_1_TABS.reduce((acc, currentTab, tabIndex) => {
    if (
      (!(hasSelfScope || hasGlobalScope) && tabIndex === 0) ||
      (!(hasDRScope || hasGlobalScope) && tabIndex === 2)
    ) {
      return acc;
    }

    return [
      ...acc,
      {
        ...currentTab,
        name: labels[currentTab.key].name,
        infoText: labels[currentTab.key].infoText,
      },
    ];
  }, []);
};

export const prepareActionPlanTabs = (labels, accessControl) => {
  const hasSelfScope = hasRequiredScopeCheck(
    PERMISSION_CODES.PLANS_VIEW,
    PERMISSION_SCOPES.SELF,
    accessControl
  );
  const hasGlobalScope = hasRequiredScopeCheck(
    PERMISSION_CODES.PLANS_VIEW,
    PERMISSION_SCOPES.GLOBAL,
    accessControl
  );
  const hasDRScope = hasRequiredScopeCheck(
    PERMISSION_CODES.PLANS_VIEW,
    PERMISSION_SCOPES.DR,
    accessControl
  );

  return DASHBOARD_PLAN_TABS.reduce((acc, currentTab, tabIndex) => {
    if (
      (!(hasSelfScope || hasGlobalScope) && tabIndex === 0) ||
      (!(hasDRScope || hasGlobalScope) && tabIndex === 2)
    ) {
      return acc;
    }

    return [
      ...acc,
      {
        ...currentTab,
        name: labels[currentTab.key].name,
        infoText: labels[currentTab.key].infoText,
      },
    ];
  }, []);
};
